/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { PriceComponent } from '../../../themes/adventureworks/views/components/price.components';
import { ICoreContext, IImageSettings, Image, ITelemetry } from '@msdyn365-commerce/core';
import { ProductListLine } from '@msdyn365-commerce/retail-proxy';
import { FeatureState, ProductCatalog, ProductPrice, SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { ArrayExtensions, getFallbackImageUrl, validateCatalogId } from '@msdyn365-commerce-modules/retail-actions';
import { Button, INodeProps } from '@msdyn365-commerce-modules/utilities';
import React, { useState } from 'react';
// import { format } from '@msdyn365-commerce/retail-proxy';
import  { IOrderTemplateLineViewModel } from '../order-template';
import OrderTemplateQuantity from './common/order-template-quantity';
import { IAlternateItems } from '../../buybox/components/buybox-components';
import { ConfirmAddedToCartDialog } from '../components/add-to-cart-confirmation-dialog-component';
import { IAlternateProduct } from '../../../themes/adventureworks/views/common';
import { ProductNotPurchasableComponent } from '../../../themes/adventureworks/views/components/productnotpurchasable.component';
// import ProductsImport from '../../products-import/products-import';

export interface IOrderTemplateLineProps extends ProductListLine {
    orderTemplateLine: IOrderTemplateLineViewModel;
    productListLine: ProductListLine;
    price?: ProductPrice;
    context: ICoreContext;
    productUrl: string;
    imageSettings?: IImageSettings;
    telemetry: ITelemetry;
    moduleId: string;
    moduleTypeName: string;
    quantity: number;
    inventoryInformationLabel?: string;
    isQuantityDisabledForUpdate?: boolean;
    isConfirmPopupOpen?:boolean;
    handlers: {
        onAddToBag(product: SimpleProduct, quantity: number, itemKey: string,inventoryLabel?:string,alternateProd?:IAlternateProduct,alternateItem?:IAlternateItems, isBackOrder?: boolean, isOversized?: boolean, isAlternativeOversized?: boolean): void;
        onRemoveItem(line: IOrderTemplateLineViewModel): void;
        onChangeQuantity(orderTemplateLineId: string, quantity: number, props?: IOrderTemplateLineProps): void;
        onSelect(line: IOrderTemplateLineViewModel, isChecked: boolean): void;
        updateConfirmPopupState(isOpenPopup:boolean,isConsentGiven?:boolean): void;
    };
    resources: {
        selectOneRowText?: string;
        moreButtonText?: string;
        viewProductDetailsText?: string;
        productDimensionTypeColor: string;
        productDimensionTypeSize: string;
        productDimensionTypeStyle: string;
        originalPriceText: string;
        currentPriceText: string;
        freePriceText: string;
        inputQuantityAriaLabel: string;
        removeFromOrderTemplateLinesText: string;
        addToBagButtonText: string;
        deleteButtonText: string;
        incrementButtonAriaLabel?: string;
        decrementButtonAriaLabel?: string;
        editOrderTemplateLineQuantityError?: string;
        unavailableProductErrorMessage?: string;
        outOfStockProductErrorMessage?: string;
        confirmBodyText?:string;
        confirmHeaderText?:string;
        alterItemBodyText:string;
        alterItemHeaderText:string;
    };
    index: number;
    isMobile: boolean;
    productQuantityView?: React.ReactNode;
    addToBagButton?: React.ReactNode;
    removeButton?: React.ReactNode;
    toggleDetailsDisplayed(props: IOrderTemplateLineProps): void;
	isMultipleCatalogFeatureEnabledInHq?: boolean;
    catalogs?: ProductCatalog[];
    error?: string;
    shouldDisable?: boolean;
    featureState?: FeatureState[];
}

export interface IOrderTemplateLineViewProps {
    key?: string;
    productImage: React.ReactNode;
    productPrice?: React.ReactNode;
    addToBagButton?: React.ReactNode;
    removeButton?: React.ReactNode;
    productName?: string;
    productNumber?: React.ReactNode;
    productInfo?: React.ReactNode;
    productLabel?: React.ReactNode;
    productUnitOfMeasure?: React.ReactNode;
    productQuantity?: number;
    productQuantityView?: React.ReactNode;
    productListId?: string;
    selectLine?: React.ReactNode;
    popUp?: JSX.Element;
    extraActionsPopup?: JSX.Element | null | undefined;
    isMobile?: boolean;
    isMultipleCatalogFeatureEnabledInHq?: boolean;
    productLineCatalog?: React.ReactNode;
    disabled?: boolean;
    confirmPopUp?: React.ReactNode;
}

interface IEventHandlerFactory {
    getAddToBag(): (event: React.MouseEvent<HTMLElement>) => void;
    getRemoveItem(): (event: React.MouseEvent<HTMLElement>) => void;
}

const OrderTemplateLineActions = (props: IOrderTemplateLineProps): IEventHandlerFactory => {
    const { orderTemplateLine, productListLine, quantity, handlers,inventoryInformationLabel } = props;
    const { LineId } = productListLine;
    const { onAddToBag, onRemoveItem } = handlers;

    return {
        getAddToBag: () => (event: React.MouseEvent<HTMLElement>) => {
            LineId && orderTemplateLine.fullProduct && onAddToBag(orderTemplateLine.fullProduct.ProductDetails, quantity, LineId,inventoryInformationLabel,orderTemplateLine.fullProduct.AlternateProduct?.currentProd,orderTemplateLine.fullProduct.AlternateProduct?.serviceResp && orderTemplateLine.fullProduct.AlternateProduct?.serviceResp[0],false,orderTemplateLine.fullProduct.IsOversizedItem,orderTemplateLine.fullProduct.IsAlternateOversized);
        },
        getRemoveItem: () => (event: React.MouseEvent<HTMLElement>) => LineId && onRemoveItem(props.orderTemplateLine)
    };
};

interface IPopupProps extends IOrderTemplateLineProps {
    addButtonClassName: string;
    removeButtonClassName: string;
}

const PopUp = (props: IPopupProps): JSX.Element | null => {
    const { addToBagButtonText, removeFromOrderTemplateLinesText, moreButtonText, viewProductDetailsText } = props.resources;
    const [showMobile, setShowMobile] = useState(false);
    const componentClassName = 'ms-table';

    const getViewHandler = () => (event: React.MouseEvent<HTMLElement>) => {
        props.toggleDetailsDisplayed(props);
    };

    const toggle = React.useCallback(() => {
        setShowMobile(!showMobile);
    }, [showMobile]);

    return (
        <div className={`${componentClassName}__row-links-minified`}>
            <Button
                className={`${componentClassName}__row-links-toggle`}
                aria-label={moreButtonText}
                data-type={props.index}
                onClick={toggle}
            />
            {showMobile &&
            <div className={`${componentClassName}__row-links`}>
                <Button
                    className='ms-table__row-links-view'
                    onClick={getViewHandler()}
                    title={viewProductDetailsText}
                    aria-label={viewProductDetailsText}
                >
                    View
                </Button>
                {getActionButton(props.isMobile, props.removeButtonClassName,
                    removeFromOrderTemplateLinesText, OrderTemplateLineActions(props).getRemoveItem())}
                {getActionButton(props.isMobile, props.addButtonClassName, addToBagButtonText, OrderTemplateLineActions(props).getAddToBag())}
            </div>}
        </div>
    );
};

const ConfirmPopUpElm = (input: IOrderTemplateLineProps): JSX.Element | null => {
    // const { addToBagButtonText, removeFromOrderTemplateLinesText, moreButtonText, viewProductDetailsText } = props.resources;

//const [isSuppresedPopUp, setPopState] = useState(false);

//let isSuppresed = input.orderTemplateLine.fullProduct?.AlternateProduct?.serviceResp && input.orderTemplateLine.fullProduct?.AlternateProduct?.serviceResp[0].UseAltItemId === 'Always' && input.orderTemplateLine.fullProduct?.AlternateProduct.currentProd ? true : false;

// React.useCallback(() => {
//     setPopState(isSuppresed);
// }, [isSuppresedPopUp]);


const closeConfirmAddedToCartDialog = () => {
    input.handlers.updateConfirmPopupState(false,false);
    // setDisabled(false);
};

const cofimationyesEvent =() => {
    input.handlers.updateConfirmPopupState(false,true);
    setTimeout(() => {
        if(input.productListLine.LineId && input.orderTemplateLine.fullProduct)
    {
        input.handlers.onAddToBag(input.orderTemplateLine.fullProduct.ProductDetails, input.quantity, input.productListLine.LineId, input.inventoryInformationLabel,input.orderTemplateLine.fullProduct.AlternateProduct?.currentProd,input.orderTemplateLine.fullProduct.AlternateProduct?.serviceResp && input.orderTemplateLine.fullProduct.AlternateProduct?.serviceResp[0], true);
    }
    }, 1000);

    // WishListItemActions.addToCart(input);
    };


    return (

            <ConfirmAddedToCartDialog
            className='msc-confirm-added-to-cart'
            modalHeader={input.resources.confirmHeaderText!}
            modalBodyText={input.resources.confirmBodyText!}
            modalOpen={input.isConfirmPopupOpen}
            onClose={closeConfirmAddedToCartDialog}
            alternateItem={false}
            _addItemToCartCustom={cofimationyesEvent}
            quantity={1}
            onNoClick={closeConfirmAddedToCartDialog}
            />
    );
};

const getActionButton = (isMobile: boolean, className: string, buttonText: string, clickHandler: (event: React.MouseEvent<HTMLElement>) => void): React.ReactNode => (
    <Button
        aria-label={buttonText}
        className={className}
        onClick={clickHandler}
        title={buttonText}
    >
        {isMobile ? buttonText : ''}
    </Button>
);

const getDesktopActionButton = (
    className: string,
    buttonText: string,
    clickHandler: (event: React.MouseEvent<HTMLElement>) => void
): React.ReactNode => (
    <Button aria-label={buttonText} className={className} onClick={clickHandler} title={buttonText}>
        {buttonText}
    </Button>
);

/**
 * On Select line function.
 * @param orderTemplateLine -Order template line.
 * @param input -Order template line props.
 * @returns Set on select order template.
 */
const onSelectLineHandler = (
    orderTemplateLine: IOrderTemplateLineViewModel,
    input: IOrderTemplateLineProps) => (event: React.ChangeEvent<HTMLInputElement>) => {
    orderTemplateLine.isChecked = !orderTemplateLine.isChecked;

    input.handlers.onSelect(orderTemplateLine, event.target.checked);
};

export const OrderTemplateLineView = (input: IOrderTemplateLineProps): IOrderTemplateLineViewProps | null => {
    const { orderTemplateLine, productListLine, price, context, imageSettings, quantity, resources, isMobile, isMultipleCatalogFeatureEnabledInHq } = input;
    const product = orderTemplateLine.fullProduct;
    const { LineId, UnitOfMeasure } = productListLine;
    const { removeFromOrderTemplateLinesText, addToBagButtonText, selectOneRowText } = resources;
    if (!product) {
        return null;
    }
    let isActive = false;
    let isPriceAvailable = false;
    const isPreProduct = false;

        if(product && product.ProductDetails && product.ProductDetails.ExtensionProperties)
        {
            const isActiveProps = product.ProductDetails.ExtensionProperties.filter(x=> x.Key === 'IsActive');
            if(!isActive && isActiveProps && isActiveProps[0] && isActiveProps[0].Value !== undefined)
            {
                isActive= isActiveProps[0].Value.BooleanValue !== undefined ? isActiveProps[0].Value.BooleanValue : false;
            }
             const isPreProductExt = product.ProductDetails.ExtensionProperties.find(x=> x.Key === 'IsPreProductStatus');
            if(isPreProductExt && isPreProductExt.Value)
            {
                isActive= isPreProductExt.Value.BooleanValue ?? false;
            }
        }
        if(product && product.ProductPrice)
        {
            if(product.TradeAgreement && product.TradeAgreement.TradeAgreementPrice !== 0)
            {
                isPriceAvailable = true;
            }
        }

    const { ItemId, PrimaryImageUrl } = product.ProductDetails;
     const fallbackImage = getFallbackImageUrl(
        ItemId,
        context.actionContext.requestContext.apiSettings,
        context.request.app?.config?.OmniChannelMedia
    );
    const addButtonClassName = input.isMobile ? 'ms-table__row-links-add' : 'ms-order-template-table-line__product-add-button';
    const removeButtonClassName = input.isMobile ? 'ms-table__row-links-delete' : 'ms-order-template-table-line__product-remove-button';

    return {
        isMobile,
        isMultipleCatalogFeatureEnabledInHq: isMultipleCatalogFeatureEnabledInHq,
        selectLine: isActive && isPriceAvailable?(
            <label className='checkbox-container'>
                <input
                    aria-label={selectOneRowText} className='checkbox-input'
                    type='checkbox' checked={orderTemplateLine.isChecked} aria-checked={orderTemplateLine.isChecked}
                    onChange={onSelectLineHandler(orderTemplateLine, input)} />
                <span className='checkmark' />
            </label>
        ): undefined,
        key: LineId!,
        productLineCatalog: isMultipleCatalogFeatureEnabledInHq && renderProductCatalog(input),
        productImage: _renderProductImage(
            'ms-order-template-table-line__product-image',
            context,
            product.ProductDetails.Name!,
            PrimaryImageUrl,
            fallbackImage,
            imageSettings
        ),
        productInfo: renderProductInfo(input),
        productPrice: isActive && isPriceAvailable && price && _renderPrice(input),
        productNumber: product && (
            <>
                <div className='ms-order-template-table-line__product-number'>{ItemId}</div>
                {input.error !== 'NONE' ? (
                    <div className='ms-order-template-table-line__product-availability'>{_renderProductError(input)}</div>
                ) : (
                    undefined
                )}
            </>
        ),
        productUnitOfMeasure: price ? (
            <div className='ms-order-template-table-line__product-unit-of-measure'>{UnitOfMeasure || price.UnitOfMeasure}</div>
        ) : (
            undefined
        ),
        productListId: LineId!,
        removeButton: getActionButton(input.isMobile, removeButtonClassName, removeFromOrderTemplateLinesText, OrderTemplateLineActions(input).getRemoveItem()),
        productQuantityView: isActive && isPriceAvailable ? (<div>
            {_generateQuantity(input, LineId!)}
        </div>) : undefined,
        productQuantity: quantity,
        addToBagButton:isActive && isPriceAvailable && !isMultipleCatalogFeatureEnabledInHq ? getActionButton(input.isMobile, addButtonClassName, addToBagButtonText, OrderTemplateLineActions(input).getAddToBag()): !isActive || !isPriceAvailable ?<ProductNotPurchasableComponent
        isInActiveProduct={!isActive}
        isPriceUnavailable={!isPriceAvailable}
        isPreProduct={isPreProduct}
        />:<></>,
          extraActionsPopup:
            !isMobile && isMultipleCatalogFeatureEnabledInHq ? (
                <ExtraActionsPopup {...input} addButtonClassName={addButtonClassName} removeButtonClassName={removeButtonClassName} />
            ) : (
                undefined
            ),
        popUp: (
            <PopUp
                {...input}
                addButtonClassName={addButtonClassName}
                removeButtonClassName={removeButtonClassName}
            />
        ),
        disabled: !isActive || !isPriceAvailable ? false : input.shouldDisable,
        confirmPopUp:(
            <ConfirmPopUpElm
            {...input}
            />
        )
    };
};

const ExtraActionsPopup = (props: IPopupProps): JSX.Element | null => {
    const [showActions, setShowActions] = useState(false);
    const toggle = React.useCallback(() => {
        setShowActions(!showActions);
    }, [showActions]);

    const className = 'ms-order-template-table-line__extra-actions-cell';
    const actionsContainerClassName = `${className}__actions-container`;
    return (
        <div className={className}>
            <Button className={`${className}__toggle`} aria-label={''} onClick={toggle} />
            {showActions && (
                <div className={actionsContainerClassName}>
                    {getDesktopActionButton(
                        'ms-order-template-table-lines__products-add-button',
                        'Add to bag',
                        OrderTemplateLineActions(props).getAddToBag()
                    )}
                    {getDesktopActionButton(
                        'ms-order-template-table-lines__products-remove-button',
                        'Remove item',
                        OrderTemplateLineActions(props).getRemoveItem()
                    )}
                </div>
            )}
        </div>
    );
};

export const OrderTemplateMobileLineView = (input: IOrderTemplateLineProps): IOrderTemplateLineViewProps | undefined => {
    const { orderTemplateLine, productListLine, price, context, imageSettings, isMultipleCatalogFeatureEnabledInHq } = input;
    const product = orderTemplateLine.fullProduct;
    const { LineId, UnitOfMeasure } = productListLine;
    if (!product) {
        return;
    }
    const { ItemId, PrimaryImageUrl } = product.ProductDetails;
    const fallbackImage = getFallbackImageUrl(ItemId, context.actionContext.requestContext.apiSettings, context.request.app?.config?.OmniChannelMedia);

    return {
       productLineCatalog: isMultipleCatalogFeatureEnabledInHq && renderProductCatalog(input),
        productImage: _renderProductImage(
            'thumbnail',
            context,
            product.ProductDetails.Name!,
            PrimaryImageUrl,
            fallbackImage,
            imageSettings
        ),
        productInfo: renderProductInfo(input),
        productPrice: price && _renderPrice(input),
        productNumber: ItemId,
        productUnitOfMeasure: UnitOfMeasure || price?.UnitOfMeasure,
        productQuantityView: _generateQuantity(input, LineId!)
        // confirmPopUp:(
        //     <ConfirmAddedToCartDialog
        //     className='msc-confirm-added-to-cart'
        //     modalHeader={input.orderTemplateLine.fullProduct?.AlternateProduct?.serviceResp && input.orderTemplateLine.fullProduct?.AlternateProduct?.serviceResp[0].UseAltItemId === 'Always' && input.orderTemplateLine.fullProduct?.AlternateProduct.currentProd ? input.resources.alterItemHeaderText! : input.resources.confirmHeaderText!}
        //     modalBodyText={input.orderTemplateLine.fullProduct?.AlternateProduct?.serviceResp && input.orderTemplateLine.fullProduct?.AlternateProduct?.serviceResp[0].UseAltItemId === 'Always' && input.orderTemplateLine.fullProduct?.AlternateProduct.currentProd  ? format( input.resources.alterItemBodyText!,input.orderTemplateLine.fullProduct.AlternateProduct.currentProd.ItemId)  : input.resources.confirmBodyText!}
        //     modalOpen={isConfirmAddedToCartDialogOpen}
        //     onClose={closeConfirmAddedToCartDialog}
        //     alternateItem={input.orderTemplateLine.fullProduct?.AlternateProduct?.serviceResp && input.orderTemplateLine.fullProduct?.AlternateProduct?.serviceResp[0].UseAltItemId === 'Always' && input.orderTemplateLine.fullProduct?.AlternateProduct.currentProd ? true : false}
        //     _addItemToCartCustom={cofimationyesEvent}
        //     quantity={ 1}
        //     onNoClick={closeConfirmAddedToCartDialog}
        //     />
        // )
    };
};

export interface IOrderTemplateProductQuantityViewProps {
    ContainerProps: INodeProps;
    input: React.ReactNode;
}

const _renderProductError = (props: IOrderTemplateLineProps) => {
    const { error, resources } = props;

    if (!error) {
        return '';
    }

    const errorMessages = {
        OOS: resources.outOfStockProductErrorMessage,
        UNASSORTED: resources.unavailableProductErrorMessage,
        NONE: ''
    };
    //@ts-ignore
    return errorMessages[`${error}`];
};

/**
 * On Change function.
 * @param onChangeQuantity -On change quantity function.
 * @param itemKey -String.
 * @param props -Order template line props.
 * @returns Set change quantity.
 */
const onChangeHandler = (onChangeQuantity: (orderTemplateLineId: string,
    quantity: number, props?: IOrderTemplateLineProps | undefined) => void,
itemKey: string, props: IOrderTemplateLineProps) => (newValue: number) => {
    if (onChangeQuantity) {
        onChangeQuantity(itemKey, newValue, props);
    }
};

const _generateQuantity = (props: IOrderTemplateLineProps, itemKey: string): React.ReactNode => {
    const { handlers, resources, quantity } = props;
    const { onChangeQuantity } = handlers;
    return (
        <OrderTemplateQuantity
            currentCount={quantity}
            onChange={onChangeHandler(onChangeQuantity, itemKey, props)}
            max={999_999_999}
            inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
            incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
            decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
            disabled={props.isQuantityDisabledForUpdate}
        />
    );
};

export const _renderProductImage = (
    className: string,
    context: ICoreContext,
    heading: string,
    image?: string,
    fallbackImage?: string,
    imageSettings?: IImageSettings
): JSX.Element | null => {
    const defaultImageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=64&h=64&m=6', w: 0, h: 0 },
            lg: { q: 'w=64&h=64&m=6', w: 0, h: 0 },
            xl: { q: 'w=64&h=64&m=6', w: 0, h: 0 }
        },
        lazyload: true
    };

    if (image) {
        return (
            <Image
                requestContext={context?.actionContext?.requestContext}
                className={className}
                altText={heading}
                title={heading}
                src={image}
                fallBackSrc={fallbackImage}
                gridSettings={context?.request?.gridSettings!}
                imageSettings={imageSettings || defaultImageSettings}
                loadFailureBehavior='empty'
                tabIndex={0}
            />
        );
    }
    return null;
};

export const renderProductCatalog = (props: IOrderTemplateLineProps) => {
    const catalogId = props.productListLine.CatalogId;
    validateCatalogId(catalogId);

    if (!props.context.request.user.isB2b || !catalogId || !ArrayExtensions.hasElements(props.catalogs)) {
        return null;
    }

    const catalog = props.catalogs.find(item => item.RecordId === catalogId);

    if (!catalog || !catalog.Name) {
        return null;
    }

    return <div className='msc-cart-line__catalog-label'>{catalog.Name}</div>;
};

const _renderPrice = (props: IOrderTemplateLineProps): JSX.Element | null => {
    const { price, context, moduleId, moduleTypeName, resources } = props;

    if (price) {
        return (
            <PriceComponent
                id={moduleId}
                typeName={moduleTypeName}
                data={{ price }}
                className='ms-order-template-table-line__product-price'
                freePriceText={resources.freePriceText}
                originalPriceText={resources.originalPriceText}
                currentPriceText={resources.currentPriceText}
                context={context}
            />
        );
    }
    return null;
};

const _renderProductDimensions = (props: IOrderTemplateLineProps): (string | undefined)[] => {
    const { orderTemplateLine } = props;

    if (!orderTemplateLine.fullProduct || !orderTemplateLine.fullProduct.ProductDetails.Dimensions) {
        return [];
    }

    return orderTemplateLine.fullProduct.ProductDetails.Dimensions.map(dimension => {
        if (!dimension.DimensionValue || !dimension.DimensionValue.Value) {
            return undefined;
        }

        return dimension.DimensionValue.Value;
    });
};


export const renderProductInfo = (props: IOrderTemplateLineProps): React.ReactNode | undefined => {
    const { orderTemplateLine, productUrl } = props;
    const name = orderTemplateLine.fullProduct?.ProductDetails.Name;

    const dimensions = _renderProductDimensions(props);
    if (!orderTemplateLine.fullProduct) {
        return [];
    }

    const productName = productUrl ? (
        <a className='ms-order-template-table-line__product-info-name' href={productUrl}>
            {name}
        </a>
    ) : (
        <div className='ms-order-template-table-line__product-info-name'>
            {name}
        </div>
    );

    const dimensionsLine: string = dimensions.join(', ');

    return (
        <div className='ms-order-template-table-line__product-info'>
            <div className='ms-order-template-table-line__product-info-name'>
                {productName}
            </div>
            <div className='ms-order-template-table-line__product-info-dimension'>
                {dimensionsLine}
            </div>
        </div>
    );
};
